import { r as registerInstance, e as createEvent, c as prefix, m as mapBreakpointPropToPrefixedClasses, h, g as getElement } from './breakpointCustomizable-ff31979d.js';
import { t as throttle } from './index.esm-37b6c9b0.js';
import { i as improveFocusHandlingForCustomElement } from './focusHandling-e078221d.js';
/**
 * Universal pagination model generation algorithm
 *
 * The idea behind this module is to move the logic of creating pagination out of the component
 * and place it in a separate module.
 *
 * Adapted from ultimate-pagination
 * https://github.com/ultimate-pagination/ultimate-pagination
 *
 */
var itemTypes = {
    PAGE: 'PAGE',
    ELLIPSIS: 'ELLIPSIS',
    PREVIOUS_PAGE_LINK: 'PREVIOUS_PAGE_LINK',
    NEXT_PAGE_LINK: 'NEXT_PAGE_LINK'
};
var itemKeys = {
    FIRST_ELLIPSIS: -1,
    SECOND_ELLIPSIS: -2,
    PREVIOUS_PAGE_LINK: -4,
    NEXT_PAGE_LINK: -5
};
var createFirstEllipsis = function (pageNumber) { return ({
    type: itemTypes.ELLIPSIS,
    key: itemKeys.FIRST_ELLIPSIS,
    value: pageNumber,
    isActive: false
}); };
var createSecondEllipsis = function (pageNumber) { return ({
    type: itemTypes.ELLIPSIS,
    key: itemKeys.SECOND_ELLIPSIS,
    value: pageNumber,
    isActive: false
}); };
var createPreviousPageLink = function (options) {
    var activePage = options.activePage;
    return {
        type: itemTypes.PREVIOUS_PAGE_LINK,
        key: itemKeys.PREVIOUS_PAGE_LINK,
        value: Math.max(1, activePage - 1),
        isActive: activePage > 1
    };
};
var createNextPageLink = function (options) {
    var activePage = options.activePage, pageTotal = options.pageTotal;
    return {
        type: itemTypes.NEXT_PAGE_LINK,
        key: itemKeys.NEXT_PAGE_LINK,
        value: Math.min(pageTotal, activePage + 1),
        isActive: activePage < pageTotal
    };
};
var createPageFunctionFactory = function (options) {
    var activePage = options.activePage;
    return function (pageNumber) { return ({
        type: itemTypes.PAGE,
        key: pageNumber,
        value: pageNumber,
        isActive: pageNumber === activePage
    }); };
};
var createRange = function (start, end) {
    var range = [];
    for (var i = start; i <= end; i++) {
        range.push(i);
    }
    return range;
};
var createPaginationModel = function (options) {
    // exception tests
    if (options == null) {
        throw new Error('createPaginationModel(): options object should be a passed');
    }
    var pageTotal = options.pageTotal, activePage = options.activePage, pageRange = options.pageRange;
    var boundaryPagesRange = 1;
    var ellipsisSize = 1;
    var paginationModel = [];
    var createPage = createPageFunctionFactory(options);
    paginationModel.push(createPreviousPageLink(options));
    // Simplify generation of pages if number of available items is equal or greater than total pages to show
    if (1 + 2 * ellipsisSize + 2 * pageRange + 2 * boundaryPagesRange >= pageTotal) {
        var allPages = createRange(1, pageTotal).map(createPage);
        paginationModel.push.apply(paginationModel, allPages);
    }
    else {
        // Calculate group of first pages
        var firstPagesStart = 1;
        var firstPagesEnd = boundaryPagesRange;
        var firstPages = createRange(firstPagesStart, firstPagesEnd).map(createPage);
        // Calculate group of last pages
        var lastPagesStart = pageTotal + 1 - boundaryPagesRange;
        var lastPagesEnd = pageTotal;
        var lastPages = createRange(lastPagesStart, lastPagesEnd).map(createPage);
        // Calculate group of main pages
        var mainPagesStart = Math.min(Math.max(activePage - pageRange, firstPagesEnd + ellipsisSize + 1), lastPagesStart - ellipsisSize - 2 * pageRange - 1);
        var mainPagesEnd = mainPagesStart + 2 * pageRange;
        var mainPages = createRange(mainPagesStart, mainPagesEnd).map(createPage);
        // Add group of first pages
        paginationModel.push.apply(paginationModel, firstPages);
        // Calculate and add ellipsis before group of main pages
        var firstEllipsisPageNumber = mainPagesStart - 1;
        var showPageInsteadOfFirstEllipsis = firstEllipsisPageNumber === firstPagesEnd + 1;
        var createFirstEllipsisOrPage = showPageInsteadOfFirstEllipsis ? createPage : createFirstEllipsis;
        var firstEllipsis = createFirstEllipsisOrPage(firstEllipsisPageNumber);
        paginationModel.push(firstEllipsis);
        // Add group of main pages
        paginationModel.push.apply(paginationModel, mainPages);
        // Calculate and add ellipsis after group of main pages
        var secondEllipsisPageNumber = mainPagesEnd + 1;
        var showPageInsteadOfSecondEllipsis = secondEllipsisPageNumber === lastPagesStart - 1;
        var createSecondEllipsisOrPage = showPageInsteadOfSecondEllipsis ? createPage : createSecondEllipsis;
        var secondEllipsis = createSecondEllipsisOrPage(secondEllipsisPageNumber);
        paginationModel.push(secondEllipsis);
        // Add group of last pages
        paginationModel.push.apply(paginationModel, lastPages);
    }
    paginationModel.push(createNextPageLink(options));
    return paginationModel;
};
var getCurrentActivePage = function (activePage, totalPages) {
    // exception tests
    if (activePage === undefined || totalPages === undefined) {
        throw new Error('getCurrentActivePage(): activePage and totalPages props must be provided');
    }
    // Obviously we can't be on a negative or 0 page.
    if (activePage < 1) {
        activePage = 1;
    }
    // If the user has done something like /page/99999 we want to clamp that back down.
    if (activePage > totalPages) {
        activePage = totalPages;
    }
    return activePage;
};
var getTotalPages = function (totalItemsCount, itemsPerPage) {
    // exception test
    if (totalItemsCount === undefined || itemsPerPage === undefined) {
        throw new Error('getTotalPages(): totalItemsCount and itemsPerPage props must be provided');
    }
    if (totalItemsCount < 1) {
        totalItemsCount = 1;
    }
    if (itemsPerPage < 1) {
        itemsPerPage = 1;
    }
    return Math.ceil(totalItemsCount / itemsPerPage);
};
var listeners = new Map();
var handleResize = throttle(500, function () {
    listeners.forEach(function (callback) {
        callback();
    });
});
var attachListenerIfNeeded = function () {
    if (listeners.size === 1) {
        window.addEventListener('resize', handleResize);
    }
};
var removeListenerIfNotNeededAnymore = function () {
    if (listeners.size < 1) {
        window.removeEventListener('resize', handleResize);
    }
};
var listenResize = function (callback) {
    var token = {};
    listeners.set(token, callback);
    attachListenerIfNeeded();
    return function () {
        listeners.delete(token);
        removeListenerIfNotNeededAnymore();
    };
};
var readCounterResetValue = function (element) {
    var computedStyles = window.getComputedStyle(element);
    var resetValues = computedStyles.getPropertyValue('counter-reset');
    var resetValuesAsObject = resetValues.split(' ').reduce(function (accumulator, value) {
        var _a;
        if (!accumulator.buffer) {
            return Object.assign(Object.assign({}, accumulator), { buffer: value });
        }
        return {
            buffer: null,
            result: Object.assign(Object.assign({}, accumulator.result), (_a = {}, _a[accumulator.buffer] = parseInt(value, 10), _a))
        };
    }, {
        buffer: null,
        result: {}
    });
    return resetValuesAsObject.result;
};
var paginationCss = "@charset \"UTF-8\";:host{display:block}.p-pagination{display:-ms-flexbox;display:flex;-ms-flex-pack:center;justify-content:center;margin:0;padding:0}.p-pagination--size-5{counter-reset:size 5}.p-pagination--size-7{counter-reset:size 7}@media (min-width: 480px){.p-pagination--size-5-xs{counter-reset:size 5}.p-pagination--size-7-xs{counter-reset:size 7}}@media (min-width: 760px){.p-pagination--size-5-s{counter-reset:size 5}.p-pagination--size-7-s{counter-reset:size 7}}@media (min-width: 1000px){.p-pagination--size-5-m{counter-reset:size 5}.p-pagination--size-7-m{counter-reset:size 7}}@media (min-width: 1300px){.p-pagination--size-5-l{counter-reset:size 5}.p-pagination--size-7-l{counter-reset:size 7}}@media (min-width: 1760px){.p-pagination--size-5-xl{counter-reset:size 5}.p-pagination--size-7-xl{counter-reset:size 7}}.p-pagination__items{display:-ms-flexbox;display:flex;margin:0;padding:0}.p-pagination__item{list-style-type:none;margin:0;padding:0}.p-pagination__goto,.p-pagination__prev,.p-pagination__next{display:-ms-flexbox;display:flex;-ms-flex-pack:center;justify-content:center;-ms-flex-align:center;align-items:center;-webkit-transition:color 0.24s ease, outline-color 0.24s ease;transition:color 0.24s ease, outline-color 0.24s ease;position:relative;width:2.5rem;height:2.5rem;-webkit-box-sizing:border-box;box-sizing:border-box;outline:none transparent}.p-pagination__goto:focus,.p-pagination__prev:focus,.p-pagination__next:focus{outline-width:2px;outline-style:solid}.p-pagination--theme-light .p-pagination__goto:focus,.p-pagination--theme-light .p-pagination__prev:focus,.p-pagination--theme-light .p-pagination__next:focus{outline-color:#00d5b9}.p-pagination--theme-dark .p-pagination__goto:focus,.p-pagination--theme-dark .p-pagination__prev:focus,.p-pagination--theme-dark .p-pagination__next:focus{outline-color:#00d5b9}.p-pagination__goto:active,.p-pagination__prev:active,.p-pagination__next:active{outline:none}.p-pagination--theme-light .p-pagination__goto:active,.p-pagination--theme-light .p-pagination__prev:active,.p-pagination--theme-light .p-pagination__next:active{color:#d5001c}.p-pagination--theme-dark .p-pagination__goto:active,.p-pagination--theme-dark .p-pagination__prev:active,.p-pagination--theme-dark .p-pagination__next:active{color:#d5001c}.p-pagination__goto{font-size:1rem;line-height:1.5;font-family:\"Porsche Next\", \"Arial Narrow\", Arial, sans-serif;font-weight:400;text-decoration:none;cursor:pointer}.p-pagination--theme-light .p-pagination__goto{color:#000}.p-pagination--theme-dark .p-pagination__goto{color:#fff}.p-pagination--theme-light .p-pagination__goto:hover{color:#d5001c}.p-pagination--theme-dark .p-pagination__goto:hover{color:#d5001c}.p-pagination__goto--current{cursor:default}.p-pagination--theme-light .p-pagination__goto--current:hover{color:#000}.p-pagination--theme-dark .p-pagination__goto--current:hover{color:#fff}.p-pagination__goto--current::after{content:\"\";display:block;position:absolute;bottom:0;left:0.375rem;right:0.375rem;height:0.25rem}.p-pagination--theme-light .p-pagination__goto--current::after{background-color:#d5001c}.p-pagination--theme-dark .p-pagination__goto--current::after{background-color:#d5001c}.p-pagination__goto--ellipsis{cursor:default}.p-pagination--theme-light .p-pagination__goto--ellipsis:hover{color:#000}.p-pagination--theme-dark .p-pagination__goto--ellipsis:hover{color:#fff}.p-pagination__goto--ellipsis::after{content:\"…\"}.p-pagination__prev,.p-pagination__next{text-decoration:none;cursor:pointer}.p-pagination--theme-light .p-pagination__prev,.p-pagination--theme-light .p-pagination__next{color:#000}.p-pagination--theme-dark .p-pagination__prev,.p-pagination--theme-dark .p-pagination__next{color:#fff}.p-pagination--theme-light .p-pagination__prev:hover,.p-pagination--theme-light .p-pagination__next:hover{color:#d5001c}.p-pagination--theme-dark .p-pagination__prev:hover,.p-pagination--theme-dark .p-pagination__next:hover{color:#d5001c}.p-pagination__prev--disabled,.p-pagination__next--disabled{cursor:default}.p-pagination--theme-light .p-pagination__prev--disabled,.p-pagination--theme-light .p-pagination__next--disabled{color:#96989a}.p-pagination--theme-dark .p-pagination__prev--disabled,.p-pagination--theme-dark .p-pagination__next--disabled{color:#7c7f81}.p-pagination--theme-light .p-pagination__prev--disabled:hover,.p-pagination--theme-light .p-pagination__next--disabled:hover{color:#96989a}.p-pagination--theme-dark .p-pagination__prev--disabled:hover,.p-pagination--theme-dark .p-pagination__next--disabled:hover{color:#7c7f81}";
var Pagination = /** @class */ (function () {
    function Pagination(hostRef) {
        registerInstance(this, hostRef);
        /** The total count of items. */
        this.totalItemsCount = 1;
        /** The total count of items which should be shown per page.  */
        this.itemsPerPage = 1;
        /** Index of the currently active page. */
        this.activePage = 1;
        /** The maximum number of page links rendered */
        this.maxNumberOfPageLinks = {
            base: 5,
            xs: 7
        };
        /** Aria label what the pagination is used for. */
        this.allyLabel = 'Pagination';
        /** Aria label for previous page icon. */
        this.allyLabelPrev = 'Previous page';
        /** Aria label for page navigation. */
        this.allyLabelPage = 'Page';
        /** Aria label for next page icon. */
        this.allyLabelNext = 'Next page';
        /** Adapts the color when used on dark background. */
        this.theme = 'light';
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.unlistenResize = function () { };
        this.pageChange = createEvent(this, "pageChange", 7);
    }
    Pagination.prototype.onActivePageChange = function (page, previousPage) {
        this.pageChange.emit({ page: page, previousPage: previousPage });
    };
    Pagination.prototype.componentDidLoad = function () {
        var _this = this;
        improveFocusHandlingForCustomElement(this.element);
        this.unlistenResize = listenResize(function () {
            _this.updateMaxNumberOfPageLinks();
        });
        this.updateMaxNumberOfPageLinks();
    };
    Pagination.prototype.disconnectedCallback = function () {
        this.unlistenResize();
    };
    Pagination.prototype.render = function () {
        var _a;
        var _this = this;
        var pageRange = this.breakpointMaxNumberOfPageLinks === 7 ? 1 : 0;
        var paginationClasses = Object.assign((_a = {}, _a[prefix('pagination')] = true, _a[prefix("pagination--theme-" + this.theme)] = true, _a), mapBreakpointPropToPrefixedClasses('pagination--size', this.maxNumberOfPageLinks));
        var paginationItemsClasses = prefix('pagination__items');
        var pageTotal = getTotalPages(this.totalItemsCount, this.itemsPerPage);
        var activePage = getCurrentActivePage(this.activePage, pageTotal);
        // generate pagination items
        var createPaginationItems = function () {
            var paginationModel = createPaginationModel({
                activePage: activePage,
                pageTotal: pageTotal,
                pageRange: pageRange
            });
            var pageItems = [];
            var prevItem;
            var nextItem;
            var paginationItemClasses = prefix('pagination__item');
            paginationModel.forEach(function (pageModel) {
                var _a, _b, _c, _d;
                if (pageModel.type === itemTypes.PREVIOUS_PAGE_LINK) {
                    var paginationPrevClasses = (_a = {},
                        _a[prefix('pagination__prev')] = true,
                        _a[prefix('pagination__prev--disabled')] = !pageModel.isActive,
                        _a);
                    return (prevItem = (h("li", Object.assign({}, pageModel, { class: paginationItemClasses }), h("span", { class: paginationPrevClasses, role: 'button', tabIndex: pageModel.isActive ? 0 : null, onClick: function () { return _this.onClick(pageModel.value); }, onKeyDown: function (e) { return _this.onKeyDown(e, pageModel.value); }, "aria-disabled": !pageModel.isActive ? 'true' : null, "aria-label": _this.allyLabelPrev }, h("p-icon", { name: "arrow-head-left", color: "inherit" })))));
                }
                if (pageModel.type === itemTypes.ELLIPSIS) {
                    var paginationGoToClasses = (_b = {},
                        _b[prefix('pagination__goto')] = true,
                        _b[prefix('pagination__goto--ellipsis')] = true,
                        _b);
                    pageItems.push(h("li", Object.assign({}, pageModel, { class: paginationItemClasses }), h("span", { class: paginationGoToClasses })));
                }
                if (pageModel.type === itemTypes.PAGE) {
                    var paginationGoToClasses = (_c = {},
                        _c[prefix('pagination__goto')] = true,
                        _c[prefix('pagination__goto--current')] = pageModel.isActive,
                        _c);
                    pageItems.push(h("li", Object.assign({}, pageModel, { class: paginationItemClasses }), h("span", { class: paginationGoToClasses, role: 'button', tabIndex: pageModel.isActive ? null : 0, "aria-disabled": pageModel.isActive ? 'true' : null, onClick: function () { return _this.onClick(pageModel.value); }, onKeyDown: function (e) { return _this.onKeyDown(e, pageModel.value); }, "aria-label": _this.allyLabelPage + " " + pageModel.value, "aria-current": pageModel.isActive ? 'page' : null }, pageModel.value)));
                }
                if (pageModel.type === itemTypes.NEXT_PAGE_LINK) {
                    var paginationNextClasses = (_d = {},
                        _d[prefix('pagination__next')] = true,
                        _d[prefix('pagination__next--disabled')] = !pageModel.isActive,
                        _d);
                    return (nextItem = (h("li", Object.assign({}, pageModel, { class: paginationItemClasses }), h("span", { class: paginationNextClasses, role: 'button', tabIndex: pageModel.isActive ? 0 : null, onClick: function () { return _this.onClick(pageModel.value); }, onKeyDown: function (e) { return _this.onKeyDown(e, pageModel.value); }, "aria-disabled": !pageModel.isActive ? 'true' : null, "aria-label": _this.allyLabelNext }, h("p-icon", { name: "arrow-head-right", color: "inherit" })))));
                }
            });
            return {
                prevItem: prevItem,
                pageItems: pageItems,
                nextItem: nextItem
            };
        };
        var paginationItems = createPaginationItems();
        return (h("nav", { class: paginationClasses, role: "navigation", "aria-label": this.allyLabel, ref: function (el) { return (_this.navigationElement = el); } }, h("ul", { class: paginationItemsClasses }, paginationItems.prevItem, paginationItems.pageItems, paginationItems.nextItem)));
    };
    Pagination.prototype.onKeyDown = function (event, page) {
        /**
         * from https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
         */
        if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
            /**
             * Prevent the default action to stop scrolling when space is pressed
             */
            event.preventDefault();
            this.onClick(page);
        }
    };
    Pagination.prototype.onClick = function (page) {
        if (page !== this.activePage) {
            this.activePage = page;
        }
    };
    Pagination.prototype.updateMaxNumberOfPageLinks = function () {
        var size = readCounterResetValue(this.navigationElement).size;
        this.breakpointMaxNumberOfPageLinks = size;
    };
    Object.defineProperty(Pagination.prototype, "element", {
        get: function () { return getElement(this); },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Pagination, "watchers", {
        get: function () {
            return {
                "activePage": ["onActivePageChange"]
            };
        },
        enumerable: false,
        configurable: true
    });
    return Pagination;
}());
Pagination.style = paginationCss;
export { Pagination as p_pagination };
